/**
 * Hired Equipment adapters over the Module A/B session APIs.
 *
 * POs come from `purchaseOrderService`; vendor rates from `vendorService`.
 * Tasks sit on each PurchaseOrderLineItem (`taskId` / `task`). Prefill copies
 * that per-line task; HE-F13 filters the picker to the unique tasks on the PO.
 */

import type { PurchaseOrder } from './purchaseOrderService';
import { purchaseOrderService } from './purchaseOrderService';
import type { Vendor, VendorResource } from './vendorService';
import { vendorService } from './vendorService';
import type {
  HiredEquipmentPoResource,
  HiredEquipmentPurchaseOrder,
  TaskOption,
} from '../features/hired-equipment/types';
import { catalogueAttributes } from '../features/hired-equipment/utils/masterResources';
import { mapPurchaseOrderToPoResources } from '../features/hired-equipment/utils/poPrefill';
import {
  mergeTaskOptions,
  uniqueTasksFromPoResources,
  uniqueTasksFromPurchaseOrder,
} from '../features/hired-equipment/utils/poTaskFilter';

export type { HiredEquipmentPoResource, HiredEquipmentPurchaseOrder };

export interface HiredEquipmentPoPrefillResult {
  resources: HiredEquipmentPoResource[];
  tasks: TaskOption[];
  /**
   * The PO's own number/description/vendor, straight from `getPurchaseOrderById` — unlike
   * `getActivePosForVendor`, this is fetched by id, not filtered to ACTIVE POs for a vendor. A
   * grid line (or the anchor) can carry a PO that's no longer ACTIVE (e.g. now fully invoiced),
   * and that PO would otherwise never surface a description anywhere in the UI.
   */
  po: { id: number; poNumber: string; vendorId: number | null; description: string | null };
}

const poCache = new Map<number, HiredEquipmentPoPrefillResult>();

/**
 * V3-036: one call per data type. Opening the PO picker asked `purchase-orders/all` for the same
 * vendor twice (the New Hired Equipment chooser, then the grid; and twice again in a dev build),
 * and every PO in the list fetched its vendor's whole catalogue. Concurrent and repeated asks
 * now share one request: in-flight promises are reused, and results are kept briefly.
 */
const ACTIVE_POS_TTL_MS = 30_000;
const activePosCache = new Map<string, { at: number; promise: Promise<HiredEquipmentPurchaseOrder[]> }>();
const vendorCache = new Map<number, { at: number; promise: Promise<Vendor> }>();
const poInFlight = new Map<number, Promise<HiredEquipmentPoPrefillResult>>();

function cachedVendor(vendorId: number): Promise<Vendor> {
  const hit = vendorCache.get(vendorId);
  if (hit && Date.now() - hit.at < ACTIVE_POS_TTL_MS) return hit.promise;
  const promise = vendorService.getVendorById(vendorId);
  vendorCache.set(vendorId, { at: Date.now(), promise });
  promise.catch(() => vendorCache.delete(vendorId));
  return promise;
}

/** Tests only: forget every cached PO, vendor and PO list. */
export function resetHiredEquipmentPoCache(): void {
  poCache.clear();
  activePosCache.clear();
  vendorCache.clear();
  poInFlight.clear();
}

function catalogueMaps(resources: VendorResource[], serviceAreaId: number) {
  const rateByResourceId = new Map<number, number | null>();
  const vehicleTypeByResourceId = new Map<number, string | null>();
  const overtimeEligibleByResourceId = new Map<number, boolean>();
  const unitByResourceId = new Map<number, string | null>();
  for (const resource of resources) {
    const attrs = catalogueAttributes(resource, resources, serviceAreaId);
    rateByResourceId.set(resource.id, attrs.rate);
    overtimeEligibleByResourceId.set(resource.id, attrs.overtimeEligible);
    if (attrs.vehicleType) vehicleTypeByResourceId.set(resource.id, attrs.vehicleType);
    if (attrs.unit) unitByResourceId.set(resource.id, attrs.unit);
  }
  return { rateByResourceId, vehicleTypeByResourceId, overtimeEligibleByResourceId, unitByResourceId };
}

function getActivePosForVendor(
  vendorId: number,
  serviceAreaId?: number | null,
): Promise<HiredEquipmentPurchaseOrder[]> {
  const key = `${vendorId}:${serviceAreaId ?? ''}`;
  const hit = activePosCache.get(key);
  if (hit && Date.now() - hit.at < ACTIVE_POS_TTL_MS) return hit.promise;
  const promise = fetchActivePosForVendor(vendorId, serviceAreaId);
  activePosCache.set(key, { at: Date.now(), promise });
  promise.catch(() => activePosCache.delete(key));
  return promise;
}

async function fetchActivePosForVendor(
  vendorId: number,
  serviceAreaId?: number | null,
): Promise<HiredEquipmentPurchaseOrder[]> {
  const rows = await purchaseOrderService.getAllPurchaseOrders({
    vendorIds: [vendorId],
    statuses: ['ACTIVE'],
    ...(serviceAreaId != null ? { serviceAreaIds: [serviceAreaId] } : {}),
  });
  return rows
    .filter((po): po is PurchaseOrder & { vendorId: number } => po.vendorId != null)
    .map((po) => ({
      id: po.id,
      poNumber: po.poNumber,
      vendorId: po.vendorId,
      status: po.status,
      description: po.description ?? null,
    }));
}

function loadPo(poId: number): Promise<HiredEquipmentPoPrefillResult> {
  const cached = poCache.get(poId);
  if (cached) return Promise.resolve(cached);
  const inFlight = poInFlight.get(poId);
  if (inFlight) return inFlight;
  const promise = fetchPo(poId).finally(() => poInFlight.delete(poId));
  poInFlight.set(poId, promise);
  return promise;
}

async function fetchPo(poId: number): Promise<HiredEquipmentPoPrefillResult> {
  const po = await purchaseOrderService.getPurchaseOrderById(poId);
  let rateByResourceId = new Map<number, number | null>();
  let vehicleTypeByResourceId = new Map<number, string | null>();
  let overtimeByResourceId = new Map<number, boolean>();
  let unitByResourceId = new Map<number, string | null>();
  if (po.vendorId != null) {
    try {
      // One catalogue call per vendor, however many of its POs are listed (V3-036).
      const vendor = await cachedVendor(po.vendorId);
      const maps = catalogueMaps(vendor.resources ?? [], po.serviceAreaId);
      rateByResourceId = maps.rateByResourceId;
      vehicleTypeByResourceId = maps.vehicleTypeByResourceId;
      overtimeByResourceId = maps.overtimeEligibleByResourceId;
      unitByResourceId = maps.unitByResourceId;
    } catch (error) {
      console.error('Failed to load vendor rates for purchase order', poId, error);
    }
  }

  const resources = mapPurchaseOrderToPoResources(
    po,
    rateByResourceId,
    vehicleTypeByResourceId,
    overtimeByResourceId,
    unitByResourceId,
  );
  const result: HiredEquipmentPoPrefillResult = {
    resources,
    tasks: mergeTaskOptions(
      uniqueTasksFromPurchaseOrder(po.lineItems ?? []),
      uniqueTasksFromPoResources(resources),
    ),
    po: {
      id: po.id,
      poNumber: po.poNumber,
      vendorId: po.vendorId ?? null,
      description: po.description ?? null,
    },
  };
  poCache.set(poId, result);
  return result;
}

async function getPoResources(poId: number): Promise<HiredEquipmentPoResource[]> {
  const { resources } = await loadPo(poId);
  return resources;
}

async function getTasksForPo(poId: number): Promise<TaskOption[]> {
  const { tasks } = await loadPo(poId);
  return tasks;
}

export const hiredEquipmentPoService = {
  getActivePosForVendor,
  getPoResources,
  getTasksForPo,
  loadPo,
};
